import React from "react";
import { useNavigate } from "react-router-dom";
import homeHeroImg01 from "@/shared/assets/img/home-hero-01.png";
import homeHeroImg02 from "@/shared/assets/img/home-hero-02.png";
import homeHeroImg03 from "@/shared/assets/img/home-hero-03.png";

/**
 * HeroSection - Main banner
 * Left: Blue banner with title and CTA
 * Right: Two feature cards (Orange + Green)
 */
export const HeroSection: React.FC = () => {
  const navigate = useNavigate();

  const handleCtaClick = () => {
    navigate("/signup");
  };

  const handleServiceClick = () => {
    navigate("/service");
  };

  const handleMagneticBlocksClick = () => {
    navigate("/support", { state: { tab: 2 } });
  };

  return (
    <section className="w-full bg-white py-6 lg:py-20">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        {/* Mobile Layout (stack vertical) */}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:gap-6 items-start">
          {/* Left - Main Blue Card */}
          <div className="w-full lg:col-span-2">
            <div
              className="relative bg-sky-500 rounded-2xl lg:rounded-3xl overflow-hidden h-64 lg:h-[400px] flex flex-col justify-start p-6 lg:p-12 bg-cover bg-right bg-no-repeat"
              style={{
                backgroundImage: `url(${homeHeroImg01})`,
              }}
            >
              {/* Overlay for better text readability - subtle */}
              <div className="absolute inset-0 bg-transparent " />

              {/* Content */}
              <div className="z-10 relative">
                <h1 className="text-white text-2xl lg:text-4xl font-bold font-['NanumSquareRoundOTF'] leading-snug mb-2 lg:mb-3">
                  지금 가입하면
                  <br />
                  <span className="text-lg lg:text-4xl">
                    7일 무료 체험 혜택!
                  </span>
                </h1>
                <p className="text-white text-xs lg:text-lg font-['Pretendard'] leading-relaxed mb-4 lg:mb-6">
                  우리 아이 맞춤 학습. 오늘 바로 만나보세요!
                </p>
                <button
                  onClick={handleCtaClick}
                  className="bg-white text-sky-500 font-bold font-['NanumSquareRoundOTF'] px-4 lg:px-6 py-2 rounded-full text-xs lg:text-base hover:bg-gray-100 transition-colors"
                >
                  신청하기
                </button>
              </div>
            </div>
          </div>

          {/* Right - Feature Cards */}
          <div className="w-full lg:w-auto flex flex-row lg:flex-col gap-3 lg:gap-4">
            {/* Orange Card */}
            <div
              className="flex-1 lg:flex-none bg-orange-500 rounded-2xl overflow-hidden p-4 lg:p-6 h-40 lg:h-48 flex flex-col justify-between relative cursor-pointer hover:shadow-lg transition-shadow"
              onClick={handleMagneticBlocksClick}
            >
              <div className="z-10">
                <h3 className="text-white font-bold font-['NanumSquareRoundOTF'] text-xs lg:text-sm leading-tight">
                  400여 가지의
                  <br />
                  자석 블럭 도안
                </h3>
              </div>
              <div
                className="z-10 w-8 h-8 lg:w-9 lg:h-9 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMagneticBlocksClick();
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="lg:w-5 lg:h-5"
                >
                  <path
                    d="M7.67021 16.3137L13.2201 10.7638L7.67021 5.21389C7.23233 4.77601 7.23233 4.06608 7.67021 3.62821C7.88914 3.40927 8.1761 3.2998 8.46305 3.2998C8.75 3.2998 9.03697 3.40927 9.25593 3.62821L15.5986 9.97091C15.8089 10.1813 15.927 10.4664 15.927 10.7638C15.927 11.0612 15.8089 11.3464 15.5986 11.5566L9.25593 17.8994C9.20112 17.9541 9.14219 18.002 9.08003 18.0431C8.64478 18.3304 8.05334 18.2825 7.67021 17.8994C7.64284 17.872 7.61718 17.8436 7.59324 17.8143C7.42511 17.6081 7.34131 17.3562 7.3418 17.1043C7.34194 17.0333 7.34879 16.9623 7.36232 16.8924C7.40338 16.6804 7.506 16.4779 7.67021 16.3137Z"
                    fill="#FF7228"
                  />
                </svg>
              </div>
              {/* Product Image */}
              <img
                src={homeHeroImg02}
                alt="자석 블럭"
                className="absolute -right-2 -bottom-2 w-32 h-32 lg:w-40 lg:h-40 object-cover "
              />
            </div>

            {/* Green Card */}
            <div
              className="flex-1 lg:flex-none bg-green-600 rounded-2xl overflow-hidden p-4 lg:p-6 h-40 lg:h-48 flex flex-col justify-between relative cursor-pointer hover:shadow-lg transition-shadow"
              onClick={handleServiceClick}
            >
              <div className="z-10 flex flex-col justify-between h-full">
                <div>
                  <h3 className="text-white font-bold font-['NanumSquareRoundOTF'] text-xs lg:text-sm leading-tight">
                    아이를 위한
                    <br />
                    학습! 살펴보기
                  </h3>
                </div>
                <div
                  className="w-8 h-8 lg:w-9 lg:h-9 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleServiceClick();
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="lg:w-5 lg:h-5"
                  >
                    <path
                      d="M7.67021 16.3137L13.2201 10.7638L7.67021 5.21389C7.23233 4.77601 7.23233 4.06608 7.67021 3.62821C7.88914 3.40927 8.1761 3.2998 8.46305 3.2998C8.75 3.2998 9.03697 3.40927 9.25593 3.62821L15.5986 9.97091C15.8089 10.1813 15.927 10.4664 15.927 10.7638C15.927 11.0612 15.8089 11.3464 15.5986 11.5566L9.25593 17.8994C9.20112 17.9541 9.14219 18.002 9.08003 18.0431C8.64478 18.3304 8.05334 18.2825 7.67021 17.8994C7.64284 17.872 7.61718 17.8436 7.59324 17.8143C7.42511 17.6081 7.34131 17.3562 7.3418 17.1043C7.34194 17.0333 7.34879 16.9623 7.36232 16.8924C7.40338 16.6804 7.506 16.4779 7.67021 16.3137Z"
                      fill="#00BE42"
                    />
                  </svg>
                </div>
              </div>

              {/* Learning Content Image */}
              <img
                src={homeHeroImg03}
                alt="학습 자료"
                className="absolute -right-4 -bottom-16 w-32 h-40 lg:w-44 lg:h-52 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
